import React, { useState, useEffect } from 'react';
import { Download, XCircle, CheckCircle, XCircle as XCircleIcon, Edit3, Printer, AlertTriangle } from 'lucide-react';

// Componente principal de detalhamento do holerite
export const PayslipDetail = ({ payslip, onClose, onApprove, onReject, onEdit, isEditing, onSave, onCancel }) => {
  const [editData, setEditData] = useState({
    regular_salary_weekday: payslip.regular_salary_weekday || 0,
    regular_salary_weekend: payslip.regular_salary_weekend || 0,
    fixed_saturday_amount: payslip.fixed_saturday_amount || 0,
    overtime_salary_weekday: payslip.overtime_salary_weekday || 0,
    overtime_salary_weekend: payslip.overtime_salary_weekend || 0,
    holiday_bonus_amount: payslip.holiday_bonus_amount || 0,
    extra_monthly_amount: payslip.extra_monthly_amount || 0,
    advance_deduction: payslip.advance_deduction || 0,
    inss_deduction: payslip.inss_deduction || 0,
    fgts_deduction: payslip.fgts_deduction || 0,
    adjustment_notes: payslip.adjustment_notes || '',
  });

  // Percentuais de desconto (padrão)
  const INSS_PERCENTAGE = 7.5; // Pode ser ajustado conforme configuração
  const FGTS_PERCENTAGE = 8;

  // Estado para rastrear se os descontos foram recalculados automaticamente
  const [autoRecalculated, setAutoRecalculated] = useState(false);

  // Função para lidar com mudanças nos campos
  const handleChange = (field, value) => {
    const numValue = value === '' ? 0 : Number(value);
    setEditData(prev => ({ ...prev, [field]: numValue }));
    setAutoRecalculated(false);
  };

  // Efeito para recalcular automaticamente INS e FG when earnings change
  useEffect(() => {
    if (!isEditing) return;

    // Calcular base para descontos (salário base + sábado)
    const baseSalary =
      Number(editData.regular_salary_weekday || 0) +
      Number(editData.regular_salary_weekend || 0) +
      Number(editData.fixed_saturday_amount || 0);

    // Calcular INS e FG automatically
    const newInss = baseSalary * (INSS_PERCENTAGE / 100);
    const newFgts = baseSalary * (FGTS_PERCENTAGE / 100);

    // Atualizar os valores apenas se não foram editados manualmente recentemente
    setEditData(prev => ({
      ...prev,
      inss_deduction: Math.round(newInss * 100) / 100,
      fgts_deduction: Math.round(newFgts * 100) / 100,
    }));

    setAutoRecalculated(true);
  }, [
    isEditing,
    editData.regular_salary_weekday,
    editData.regular_salary_weekend,
    editData.fixed_saturday_amount,
  ]);

  // Renderizar campo de input (apenas em modo de edição)
  const renderEditField = (field, label) => (
    isEditing ? (
      <input
        type="number"
        step="0.01"
        min="0"
        value={editData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        className="w-full px-2 py-1 text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
      />
    ) : (
      <span className="text-right">{formatCurrency(editData[field])}</span>
    )
  );

  const calculateTotal = () => {
    // Helper para garantir número válido
    const toNumber = (val) => {
      const n = Number(val || 0);
      return isNaN(n) || !isFinite(n) ? 0 : n;
    };
    
    const extraAdvancesTotal = toNumber(payslip.extra_advances_total || 
      (Array.isArray(payslip.extra_advances) ? 
        payslip.extra_advances.reduce((sum, a) => sum + toNumber(a.amount), 0) : 0));
    const diverseExpensesTotal = toNumber(payslip.diverse_expenses_total ||
      (Array.isArray(payslip.diverse_expenses) ?
        payslip.diverse_expenses.reduce((sum, e) => sum + toNumber(e.amount), 0) : 0));
    
    const grossSalary = toNumber(editData.regular_salary_weekday) +
      toNumber(editData.regular_salary_weekend) +
      toNumber(editData.fixed_saturday_amount) +
      toNumber(editData.overtime_salary_weekday) +
      toNumber(editData.overtime_salary_weekend) +
      toNumber(editData.holiday_bonus_amount) +
      toNumber(editData.extra_monthly_amount);
    
    const deductions = toNumber(editData.advance_deduction) + 
      diverseExpensesTotal + toNumber(editData.inss_deduction);
    
    return grossSalary + extraAdvancesTotal - deductions;
  };

  const calculateBaseSalary = () => {
    return (
      Number(editData.regular_salary_weekday || 0) +
      Number(editData.regular_salary_weekend || 0) +
      Number(editData.fixed_saturday_amount || 0)
    );
  };

  const calculateGrossSalary = () => {
    const toNumber = (val) => {
      const n = Number(val || 0);
      return isNaN(n) || !isFinite(n) ? 0 : n;
    };
    
    const extraAdvancesTotal = toNumber(payslip.extra_advances_total || 
      (Array.isArray(payslip.extra_advances) ? 
        payslip.extra_advances.reduce((sum, a) => sum + toNumber(a.amount), 0) : 0));
    
    return (
      Number(editData.regular_salary_weekday || 0) +
      Number(editData.regular_salary_weekend || 0) +
      Number(editData.fixed_saturday_amount || 0) +
      Number(editData.overtime_salary_weekday || 0) +
      Number(editData.overtime_salary_weekend || 0) +
      Number(editData.holiday_bonus_amount || 0) +
      Number(editData.extra_monthly_amount || 0) +
      extraAdvancesTotal
    );
  };

  const calculateTotalDeductions = () => {
    return (
      Number(editData.advance_deduction || 0) +
      Number(editData.inss_deduction || 0)
    );
  };

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const formatHours = (value) => {
    const hours = Number(value || 0);
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes === 0) {
      return `${wholeHours}h`;
    }
    return `${wholeHours}h ${minutes}min`;
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Rascunho' },
      PENDING_APPROVAL: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Aguardando Aprovação' },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprovado' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeitado' },
    };
    return badges[status] || badges.DRAFT;
  };

  const status = getStatusBadge(payslip.status);
  const canEdit = payslip.status === 'PENDING_APPROVAL' || payslip.status === 'DRAFT';

  // Calcular valores detalhados para exibição
  // Fórmula: salário / dias no mês / 8h = valor da hora
  const getHourlyRateDetails = () => {
    if (payslip.salary_modality !== 'MONTHLY' || !payslip.monthly_base_salary) return null;

    const salary = Number(payslip.monthly_base_salary);
    const daysInMonth = 30; // Fator fixo
    const dailyRate = salary / 30;
    const hourlyRate = salary / 220; // Padrão CLT

    return {
      salary,
      daysInMonth,
      dailyRate: Math.round(dailyRate * 100) / 100,
      hourlyRate: Math.round(hourlyRate * 100) / 100,
    };
  };

  const hourlyRateDetails = getHourlyRateDetails();

  // Recalcular valor da ausência com fórmula correta
  const getAbsenceValue = (absence) => {
    if (!hourlyRateDetails || payslip.salary_modality !== 'MONTHLY') return Number(absence.value || 0);
    if (absence.type === 'FULL_DAY' || absence.type === 'DAYS') {
      const days = absence.type === 'FULL_DAY' ? 1 : Number(absence.days || 0);
      return days * hourlyRateDetails.dailyRate;
    } else if (absence.type === 'HOURS') {
      const hours = Number(absence.hours || 0);
      return hours * hourlyRateDetails.hourlyRate;
    }
    return Number(absence.value || 0);
  };

  // Calcular total de extras (Horas extras + Adicionais)
  const totalExtras = Number(editData.overtime_salary_weekday || 0) +
    Number(editData.overtime_salary_weekend || 0) +
    Number(editData.holiday_bonus_amount || 0) +
    Number(editData.extra_monthly_amount || 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
          {/* Cabeçalho da Empresa */}
          {payslip.company && (
            <div className="bg-gray-100 px-6 py-4 border-b border-gray-300">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{payslip.company.name}</h2>
                  {payslip.company.cnpj && (
                    <p className="text-sm text-gray-600"><span className="font-medium">CNPJ:</span> {payslip.company.cnpj}</p>
                  )}
                  {(payslip.company.address || payslip.company.neighborhood || payslip.company.city) && (
                    <p className="text-sm text-gray-600 font-medium">Endereço:</p>
                  )}
                  {payslip.company.address && (
                    <p className="text-sm text-gray-600">{payslip.company.address}</p>
                  )}
                  {payslip.company.neighborhood && (
                    <p className="text-sm text-gray-600">Bairro {payslip.company.neighborhood}</p>
                  )}
                  {payslip.company.city && (
                    <p className="text-sm text-gray-600">{payslip.company.city}</p>
                  )}
                </div>
                <div className="mt-2 md:mt-0">
                  <h3 className="text-lg font-bold text-[#49943d]">RECIBO DE PAGAMENTO</h3>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-[#49943d] to-[#3d7a32] text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">HOLERITE DE PAGAMENTO</h1>
              <p className="text-sm opacity-90">{monthNames[payslip.ref_month - 1]} / {payslip.ref_year}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.text}`}>
                {status.label}
              </span>
              {canEdit && !isEditing && (
                <button
                  onClick={onEdit}
                  className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                  title="Editar"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto flex-1 p-6">
            <div className="space-y-6">
              {/* Dados do Funcionário */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h2 className="text-sm font-bold text-gray-700 uppercase mb-3 border-b border-gray-300 pb-2">
                  Dados do Funcionário
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Nome</p>
                    <p className="font-semibold text-gray-900">{payslip.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Cargo</p>
                    <p className="font-semibold text-gray-900">{payslip.role}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">E-mail</p>
                    <p className="font-semibold text-gray-900">{payslip.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Modalidade</p>
                    <p className="font-semibold text-gray-900">
                      {payslip.salary_modality === 'MONTHLY' ? 'Mensalista' : 'Horista'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Remuneração */}
              <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
                  <h2 className="text-sm font-bold text-gray-700 uppercase">Remuneração</h2>
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-200">
                    {payslip.salary_modality === 'MONTHLY' ? (
                      <>
                        <tr className="bg-gray-50">
                          <td className="px-4 py-3 text-gray-700">Salário Mensal (CLT)</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {formatCurrency(payslip.monthly_base_salary)}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-gray-700">Dias no Mês / Referência</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {hourlyRateDetails ? `${hourlyRateDetails.daysInMonth} dias` : 'N/A'}
                          </td>
                        </tr>
                        {/* Dedução de ausências na base */}
                        {payslip.absence_base_deduction > 0 && (
                          <tr className="bg-orange-50">
                            <td className="px-4 py-3 text-orange-700">
                              <span className="font-medium">(-) Dedução Ausências</span>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-red-600">
                              - {formatCurrency(payslip.absence_base_deduction)}
                            </td>
                          </tr>
                        )}
                        {/* Base INSS/FGTS - só para quem tem carteira */}
                        {payslip.has_work_card === true && (
                          <tr className="bg-gray-100 border-t-2 border-gray-300">
                            <td className="px-4 py-3 font-semibold text-gray-800">Base INSS/FGTS</td>
                            <td className="px-4 py-3 text-right font-bold text-gray-900">
                              {formatCurrency(payslip.inss_base || (Number(payslip.monthly_base_salary) - Number(payslip.absence_base_deduction || 0)))}
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <>
                        <tr className="bg-gray-50">
                          <td className="px-4 py-3 text-gray-700">Horas Dias Úteis</td>
                          <td className="px-4 py-3 text-right text-gray-900">{formatHours(payslip.regular_hours_weekday)} h</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {renderEditField('regular_salary_weekday')}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-gray-700">Horas Domingos/Feriados</td>
                          <td className="px-4 py-3 text-right text-gray-900">{formatHours(payslip.regular_hours_weekend)} h</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {renderEditField('regular_salary_weekend')}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-4 py-3 text-gray-700">Disponibilidade no Mês</td>
                          <td className="px-4 py-3 text-right text-gray-900">{formatHours(payslip.total_available_hours)} h</td>
                          <td className="px-4 py-3"></td>
                        </tr>
                      </>
                    )}

                    {/* Sábados */}
                    {Number(editData.fixed_saturday_amount || 0) > 0 && (
                      <tr>
                        <td className="px-4 py-3 text-gray-700">
                          Adicional Sábado(s) Trabalhado(s)
                          {payslip.worked_saturdays && payslip.worked_saturdays.length > 0 && (
                            <span className="text-xs text-gray-500 ml-1">
                              ({payslip.worked_saturdays.length} dia(s))
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          {renderEditField('fixed_saturday_amount')}
                        </td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Horas Extras */}
              {(Number(payslip.overtime_hours_weekday || 0) > 0 ||
                Number(payslip.overtime_hours_weekend || 0) > 0 ||
                (payslip.overtime_details && payslip.overtime_details.length > 0)) && (
                <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                  <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                    <h2 className="text-sm font-bold text-blue-800 uppercase">Horas Extras</h2>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-200">
                      <tr className="bg-gray-50">
                        <td className="px-4 py-3 text-gray-700">Horas Extras Dias Úteis</td>
                        <td className="px-4 py-3 text-right text-gray-900">{formatHours(payslip.overtime_hours_weekday)} h</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {renderEditField('overtime_salary_weekday')}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-gray-700">Horas Extras Fins de Semana</td>
                        <td className="px-4 py-3 text-right text-gray-900">{formatHours(payslip.overtime_hours_weekend)} h</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          {renderEditField('overtime_salary_weekend')}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Detalhamento por dia */}
                  {payslip.overtime_details && payslip.overtime_details.length > 0 && (
                    <div className="border-t border-gray-200">
                      <div className="px-4 py-2 bg-gray-100">
                        <h3 className="text-xs font-semibold text-gray-600 uppercase">Detalhamento</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600">Data</th>
                              <th className="px-4 py-2 text-left text-gray-600">Dia</th>
                              <th className="px-4 py-2 text-center text-gray-600">Tempo</th>
                              <th className="px-4 py-2 text-right text-gray-600">Valor Hora</th>
                              <th className="px-4 py-2 text-center text-gray-600">Mult.</th>
                              <th className="px-4 py-2 text-right text-gray-600">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {payslip.overtime_details.map((detail, idx) => {
                              const formatTime = (minutes) => {
                                const hours = Math.floor(minutes / 60);
                                const mins = minutes % 60;
                                if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
                                if (hours > 0) return `${hours}h`;
                                return `${mins}min`;
                              };

                              return (
                                <tr key={idx} className={detail.isWeekend ? 'bg-orange-50' : 'bg-yellow-50'}>
                                  <td className="px-4 py-2 text-gray-900">
                                    {String(detail.day).padStart(2, '0')}/{String(detail.month).padStart(2, '0')}/{detail.year}
                                  </td>
                                  <td className="px-4 py-2 text-gray-700">{detail.dayName}</td>
                                  <td className="px-4 py-2 text-center text-gray-700">
                                    {formatTime(detail.minutes)} ({detail.hours.toFixed(2)}h)
                                  </td>
                                  <td className="px-4 py-2 text-right text-gray-600">
                                    {hourlyRateDetails ? hourlyRateDetails.hourlyRate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : detail.hourlyRate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                  </td>
                                  <td className="px-4 py-2 text-center text-gray-600">
                                    {detail.multiplier === 1.0 ? '1x' : detail.multiplier === 1.5 ? '1.5x' : '2x'}
                                  </td>
                                  <td className="px-4 py-2 text-right font-semibold text-gray-900">
                                    {hourlyRateDetails
                                      ? (detail.hours * hourlyRateDetails.hourlyRate * detail.multiplier).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                      : Number(detail.value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                    }
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Adicionais */}
              {Number(editData.holiday_bonus_amount || 0) > 0 || Number(editData.extra_monthly_amount || 0) > 0 || (payslip.extra_advances && payslip.extra_advances.length > 0) ? (
                <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                    <h2 className="text-sm font-bold text-green-800 uppercase">Adicionais</h2>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-200">
                      {Number(editData.holiday_bonus_amount || 0) > 0 && (
                        <tr className="bg-gray-50">
                          <td className="px-4 py-3 text-gray-700">Adicional Feriados</td>
                          <td className="px-4 py-3 text-right font-semibold text-green-700">
                            {renderEditField('holiday_bonus_amount')}
                          </td>
                        </tr>
                      )}
                      {Number(editData.extra_monthly_amount || 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-gray-700">Adicional Mensal</td>
                          <td className="px-4 py-3 text-right font-semibold text-green-700">
                            {renderEditField('extra_monthly_amount')}
                          </td>
                        </tr>
                      )}
                      {/* Vales Extras - ACRÉSCIMO, não desconto */}
                      {payslip.extra_advances && payslip.extra_advances.length > 0 && (
                        <>
                          <tr className={Number(editData.holiday_bonus_amount || 0) > 0 || Number(editData.extra_monthly_amount || 0) > 0 ? '' : 'bg-gray-50'}>
                            <td className="px-4 py-3 text-gray-700">
                              Vales Extras ({payslip.extra_advances.length})
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-green-700">
                              + {formatCurrency(
                                payslip.extra_advances.reduce((sum, a) => sum + Number(a.amount || 0), 0)
                              )}
                            </td>
                          </tr>
                          {/* Detalhes dos Vales Extras */}
                          {payslip.extra_advances.map((advance, idx) => (
                            <tr key={`va-${idx}`} className="bg-green-50 text-xs">
                              <td className="px-8 py-2 text-gray-600">
                                {advance.description || 'Vale extra'}
                              </td>
                              <td className="px-4 py-2 text-right text-gray-600">
                                {formatCurrency(Number(advance.amount || 0))}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                </table>
              </div>
              ) : null}

              {/* Divergências / Ausências */}
              {payslip.absence_details && payslip.absence_details.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                  <div className="bg-orange-50 px-4 py-2 border-b border-orange-200">
                    <h2 className="text-sm font-bold text-orange-800 uppercase">Divergências / Ausências</h2>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-200">
                      {payslip.absence_details.map((absence, idx) => (
                        <tr key={`abs-${idx}`} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                          <td className="px-4 py-3 text-gray-700">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900">
                                {(() => {
                                  if (!absence.date) return '-';
                                  const clean = String(absence.date).split('T')[0];
                                  const parts = clean.split('-');
                                  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : clean;
                                })()}
                              </span>
                              <span className="text-xs text-gray-500">
                                {absence.type_description || absence.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-orange-700">
                            {absence.type === 'FULL_DAY' || absence.type === 'DAYS'
                              ? `- ${absence.days || 1} dia(s)`
                              : `- ${formatHours(absence.hours)} h`
                            }
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-red-600">
                            - {formatCurrency(getAbsenceValue(absence))}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-orange-100 border-t-2 border-orange-300">
                        <td className="px-4 py-3 font-bold text-orange-800">Total Ausências</td>
                        <td className="px-4 py-3 text-right font-bold text-orange-700">
                          - {formatCurrency(payslip.absence_details.reduce((sum, a) => sum + getAbsenceValue(a), 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-700">
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Total Bruto */}
              <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-green-800">TOTAL BRUTO</span>
                  <span className="text-2xl font-bold text-green-700">
                    {formatCurrency(calculateGrossSalary())}
                  </span>
                </div>
              </div>

              {/* Descontos */}
              <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                  <h2 className="text-sm font-bold text-red-800 uppercase">Descontos</h2>
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-200">
                    {/* INSS - só para quem tem carteira */}
                    {payslip.has_work_card === true && (
                      <tr className="bg-gray-50">
                        <td className="px-4 py-3 text-gray-700">
                          INSS ({payslip.inss_percentage || 7.5}%)
                          {payslip.salary_modality === 'MONTHLY' && (
                            <span className="text-xs text-gray-500 ml-1">
                              (Base: {formatCurrency(payslip.inss_base || payslip.monthly_base_salary)}
                              {payslip.absence_base_deduction > 0 && (
                                <span className="text-orange-600"> - {formatCurrency(payslip.absence_base_deduction)} absências = {formatCurrency(payslip.inss_base)}</span>
                              )}
                              )
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editData.inss_deduction}
                              onChange={(e) => handleChange('inss_deduction', e.target.value)}
                              className="w-24 px-2 py-1 text-right border border-gray-300 rounded"
                            />
                          ) : (
                            `- ${formatCurrency(editData.inss_deduction)}`
                          )}
                        </td>
                      </tr>
                    )}

                    {/* fgts - encargo empresa */}
                    {payslip.has_work_card === true && (
                      <tr className="bg-blue-50">
                        <td className="px-4 py-3 text-gray-700">
                          fgts - empresa ({payslip.fgts_percentage || 8}%)
                          {payslip.salary_modality === 'MONTHLY' && (
                            <span className="text-xs text-blue-500 ml-1">
                              (Base: {formatCurrency(payslip.inss_base || payslip.monthly_base_salary)}
                              {payslip.absence_base_deduction > 0 && ` após ${formatCurrency(payslip.absence_base_deduction)} ausências`}
                              )
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-blue-600">
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editData.fgts_deduction}
                              onChange={(e) => handleChange('fgts_deduction', e.target.value)}
                              className="w-24 px-2 py-1 text-right border border-gray-300 rounded"
                            />
                          ) : (
                            formatCurrency(editData.fgts_deduction || (Number(payslip.inss_base || payslip.monthly_base_salary || 0) * 0.08))
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Adiantamento Quinzenal */}
                    {Number(payslip.advance_deduction || 0) > 0 && (
                      <tr>
                        <td className="px-4 py-3 text-gray-700">Adiantamento Quinzenal</td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">
                          - {formatCurrency(payslip.advance_deduction)}
                        </td>
                      </tr>
                    )}

                    {/* Despesas Diversas */}
                    {payslip.diverse_expenses && payslip.diverse_expenses.length > 0 && (
                      <>
                        <tr>
                          <td className="px-4 py-2 text-gray-700 font-semibold" colSpan={2}>
                            Despesas Diversas ({payslip.diverse_expenses.length})
                          </td>
                        </tr>
                        {payslip.diverse_expenses.map((expense, idx) => {
                          const type = expense.expense_type_name || 'Despesa';
                          const desc = expense.description || '';
                          const installment = expense.installment_number;
                          const totalInstallments = expense.installments_count;
                          const installmentInfo = totalInstallments > 1 ? ` (${installment}/${totalInstallments})` : '';
                          return (
                            <tr key={`de-${idx}`} className="bg-red-50">
                              <td className="px-4 py-1 pl-8 text-sm text-gray-600">
                                • {type}{installmentInfo}: {desc}
                              </td>
                              <td className="px-4 py-1 text-right text-sm text-red-600">
                                - {formatCurrency(Number(expense.amount || 0))}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="border-t border-red-200">
                          <td className="px-4 py-2 text-gray-700 font-medium">
                            Subtotal Despesas Diversas
                          </td>
                          <td className="px-4 py-2 text-right font-semibold text-red-600">
                            - {formatCurrency(
                              payslip.diverse_expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0)
                            )}
                          </td>
                        </tr>
                      </>
                    )}

                    {/* Total Descontos */}
                    <tr className="bg-red-100 border-t-2 border-red-300">
                      <td className="px-4 py-3 font-bold text-red-800">TOTAL DESCONTOS</td>
                      <td className="px-4 py-3 text-right font-bold text-red-700">
                        - {formatCurrency(
                          Number(editData.inss_deduction || 0) +
                          Number(payslip.advance_deduction || 0) +
                          Number(payslip.diverse_expenses_total || 
                            (payslip.diverse_expenses?.reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0))
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Resumo Final */}
              <div className="bg-gradient-to-r from-[#49943d] to-[#3d7a32] rounded-lg p-6 text-white">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg">Total Bruto</span>
                    <span className="text-lg font-semibold">
                      {formatCurrency(calculateGrossSalary())}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg">Total Descontos</span>
                    <span className="text-lg font-semibold text-red-200">
                      - {formatCurrency(
                        Number(editData.inss_deduction || 0) +
                        Number(payslip.advance_deduction || 0) +
                        Number(payslip.diverse_expenses_total || 
                          (payslip.diverse_expenses?.reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0))
                      )}
                    </span>
                  </div>
                  {/* Total Adicionais (Vales Extras) */}
                  {(() => {
                    const extraAdvancesTotal = payslip.extra_advances_total || 
                      (payslip.extra_advances?.reduce((sum, a) => sum + Number(a.amount || 0), 0) || 0);
                    if (extraAdvancesTotal <= 0) return null;
                    return (
                      <div className="flex justify-between items-center">
                        <span className="text-lg">Total Adicionais</span>
                        <span className="text-lg font-semibold text-green-200">
                          + {formatCurrency(extraAdvancesTotal)}
                        </span>
                      </div>
                    );
                  })()}
                  <div className="border-t border-white border-opacity-30 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">SALÁRIO LÍQUIDO</span>
                      <span className="text-3xl font-bold">
                        {formatCurrency(calculateTotal())}
                      </span>
                    </div>
                    <p className="text-center text-sm mt-2 opacity-80">
                      (Total a receber)
                    </p>
                  </div>
                </div>
              </div>

              {/* Informações do FGTS - só para quem tem carteira */}
              {payslip.has_work_card === true && payslip.salary_modality === 'MONTHLY' && payslip.monthly_base_salary && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm font-bold text-blue-800">FGTS</span>
                      <span className="text-xs text-blue-600 ml-2">
                        (8% {payslip.absence_base_deduction > 0
                          ? `de R$ ${(Number(payslip.monthly_base_salary) - Number(payslip.absence_base_deduction)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (base c/ ausências)`
                          : `de R$ ${Number(payslip.monthly_base_salary).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (salário base)`
                        } - depósito empresa)
                      </span>
                    </div>
                    <span className="text-lg font-semibold text-blue-700">
                      {formatCurrency(Number(payslip.inss_base || payslip.monthly_base_salary) * 0.08)}
                    </span>
                  </div>
                </div>
              )}

              {/* Observações */}
              {payslip.adjustment_notes && (
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h3 className="text-sm font-bold text-yellow-800 uppercase mb-2">Observações</h3>
                  <p className="text-sm text-gray-700">{payslip.adjustment_notes}</p>
                </div>
              )}

              {/* Aprovações */}
              {payslip.approvals && payslip.approvals.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">Aprovações</h3>
                  <div className="flex flex-wrap gap-2">
                    {payslip.approvals.map((approval, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {approval.full_name} - {new Date(approval.approved_at).toLocaleDateString('pt-BR')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 flex items-center gap-2"
              >
                <XCircleIcon className="w-4 h-4" />
                Fechar
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Imprimir
              </button>
              {payslip.status === 'PENDING_APPROVAL' && !isEditing && (
                <>
                  <button
                    onClick={() => onReject()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Rejeitar
                  </button>
                  <button
                    onClick={() => onApprove()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Aprovar
                  </button>
                </>
              )}
              {isEditing && (
                <>
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={onSave}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Salvar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayslipDetail;
